package com.essntl.features.itinerary.ui.send_message

import CheckBoxRow
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextAlign
import core.dateformat.utils.formatToFullString
import core.dateformat.utils.parseLocalDateOrNull
import core.dateformat.utils.todayDate
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.itinerary.ui.edit_itinerary.MessageSendType
import com.essntl.features.itinerary.ui.edit_itinerary.components.messaging.MessageGroupPopup
import com.essntl.features.itinerary.ui.edit_itinerary.components.messaging.MessageType
import com.essntl.features.itinerary.ui.edit_itinerary.utils.*
import com.essntl.features.itinerary.ui.send_message.components.EssntlDemoPopup
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.EsPhoneInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import io.ktor.http.*
import kotlinx.coroutines.delay
import core.config.buildFlavor

@Composable
fun SendMessageScreen(
    state: SendMessageState,
    type: MessageSendType,
    onEvent: (SendMessageEvent) -> Unit,
    toExpand: Boolean,
    onExpandScreen: () -> Unit,
) {
    val isLoading = remember(state) { state.isLoading }
    val isFailure = remember(state) { state.isFailure }

    Box(modifier = Modifier.fillMaxSize()) {
        if (isLoading) {
            EsSpinner(
                modifier = Modifier.align(Alignment.Center),
            )
        } else if (isFailure) {
            Text(
                text = "Something went wrong",
                textAlign = TextAlign.Center,
                modifier = Modifier.align(Alignment.Center),
            )
        } else {
            val sortedItineraryServices = rememberSortedItineraryServices(state.getItineraryServices.data.orEmpty())

            val suppliers = rememberSupplier(sortedItineraryServices)

            val client = state.getItinerary.data?.proposal?.primaryClient ?: ClientModel.empty

            val travelAgent = state.getItinerary.data?.proposal?.travelAgent

            val popupTitle =
                rememberPopupTitle(
                    client = client,
                    supplier = suppliers.firstOrNull(),
                    travelAgent = travelAgent,
                    type = type,
                )

            val emailEnabled =
                rememberEmailEnabled(
                    client = client,
                    supplier = suppliers.firstOrNull(),
                    travelAgent = travelAgent,
                    type = type,
                )

            val phoneMessageEnabled = rememberPhoneNumberEnabled(
                client = client,
                supplier = suppliers.firstOrNull(),
                travelAgent = travelAgent,
                type = type,
            )

            val serviceInfosMessagesTemplate: List<String> =
                rememberServiceInfosMessagesTemplate(
                    categoryList = state.getAllCategory.data.orEmpty(),
                    profile = state.getCurrentProfile.data,
                    itineraryServices = sortedItineraryServices,
                    client = state.getItinerary.data?.proposal?.primaryClient,
                    type = type,
                )

            var isEmailChecked by rememberIsEmailChecked(type, emailEnabled)

            var isWhatsappChecked by rememberIsWhatsappChecked(type, phoneMessageEnabled)

            var isMessageChecked by remember { mutableStateOf(false) }

            var typedMessage by rememberTypedMessage(type, serviceInfosMessagesTemplate)

            var ccEmail: String? by remember { mutableStateOf(null) }

            val showsServiceInfo: Boolean = rememberShowsServiceInfo(type)

            val email: String = rememberEmail(type, suppliers.firstOrNull(), client, travelAgent)

            var toEmail: String by remember { mutableStateOf(email) }

            val telephone = rememberTelephone(type, suppliers.firstOrNull(), client, travelAgent)

            var toTelephoneNumber: String by remember { mutableStateOf(telephone ?: "") }

            var openGroupPopup by remember { mutableStateOf(false) }

            var demoPopup by remember { mutableStateOf(false) }

            val uriHandler = LocalUriHandler.current

            val messagesList = getItineraryMessages(
                categoryList = state.getAllCategory.data.orEmpty(),
                itineraryServiceList = state.getItineraryServices.data.orEmpty(),
                profile = state.getCurrentProfile.data ?: ProfileModel.empty,
                client = client,
                isEmailChecked = isEmailChecked,
                isWhatsappChecked = isWhatsappChecked,
                isMessageChecked = isMessageChecked,
                typedMessage = typedMessage,
                ccEmail = ccEmail,
                toEmail = toEmail,
                from = state.getEmails.data?.from,
                bcc = state.getEmails.data?.bcc,
                replyTo = state.getEmails.data?.replyTo,
                multiTemplateId = state.getEmails.data?.waypointMultiTemplateId,
                simpleTemplateId = state.getEmails.data?.waypointSimpleTemplateId,
                showsServiceInfo = showsServiceInfo,
                toTelephoneNumber = toTelephoneNumber,
                telephone = telephone,
            )

            LaunchedEffect(toTelephoneNumber, isWhatsappChecked) {
                if (phoneMessageEnabled &&
                    isWhatsappChecked &&
                    toTelephoneNumber.isNotEmpty()
                ) {
                    delay(500L)
                    onEvent(
                        SendMessageEvent.CheckContact(
                            contactId = toTelephoneNumber,
                        ),
                    )
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight(0.9f)
                    .verticalScroll(rememberScrollState()),
            ) {
                if (!toExpand)
                    Spacer(Modifier.height(height = 1.spacing))

                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(
                        text = popupTitle,
                        style = MaterialTheme.esTypography.h3Bold24,
                        color = MaterialTheme.esColorScheme.textPrimary,
                        modifier = Modifier.padding(horizontal = 2.5.spacing),
                    )

                    if (toExpand)
                        EsSmallOutlineIconButton(
                            onClick = onExpandScreen,
                            iconRes = EsIcons.Expand,
                            modifier = Modifier
                                .padding(end = 2.5.spacing)
                                .focusProperties { canFocus = false },
                        )
                }

                Spacer(Modifier.height(height = 2.5.spacing))

                Column(
                    verticalArrangement = Arrangement.spacedBy(1.5.spacing),
                    modifier = Modifier.padding(horizontal = 3.spacing),
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        CheckBoxRow(
                            messageType = MessageType.EMAIL,
                            enabled = emailEnabled,
                            checked = isEmailChecked,
                            onCheckedChange = {
                                isEmailChecked = it
                            },
                        )

                        if (isEmailChecked) {
                            EsInput(
                                value = toEmail,
                                onValueChange = {
                                    toEmail = it
                                },
                                placeholder = "Enter email",
                                modifier = Modifier
                                    .weight(1F)
                                    .padding(horizontal = 2.5.spacing),
                            )

                            EsInput(
                                value = ccEmail ?: "",
                                onValueChange = {
                                    ccEmail = it
                                },
                                placeholder = "Add cc email",
                                modifier = Modifier
                                    .weight(1F)
                                    .padding(horizontal = 2.5.spacing),
                            )
                        }
                    }

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        CheckBoxRow(
                            messageType = MessageType.WHATSAPP,
                            enabled = phoneMessageEnabled,
                            checked = isWhatsappChecked,
                            onCheckedChange = {
                                isWhatsappChecked = it
                            },
                        )

                        if (isWhatsappChecked) {
                            EsPhoneInput(
                                value = toTelephoneNumber.toLongOrNull() ?: 0,
                                onValueChange = { value ->
                                    toTelephoneNumber = value.toString()
                                },
                                placeholder = "Enter phone",
                                modifier = Modifier
                                    .padding(horizontal = 2.5.spacing),
                            )
                            Spacer(modifier = Modifier.weight(1F))
                        }
                    }

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        CheckBoxRow(
                            messageType = MessageType.SMS,
                            enabled = phoneMessageEnabled,
                            checked = isMessageChecked,
                            onCheckedChange = {
                                isMessageChecked = it
                            },
                        )

                        if (isMessageChecked) {
                            EsInput(
                                value = toTelephoneNumber,
                                onValueChange = {
                                    toTelephoneNumber = it
                                },
                                placeholder = "Enter phone",
                                modifier = Modifier
                                    .padding(horizontal = 2.5.spacing),
                            )
                            Spacer(modifier = Modifier.weight(1F))
                        }
                    }
                }

                if (showsServiceInfo) {
                    Spacer(Modifier.height(height = 3.spacing))

                    serviceInfosMessagesTemplate
                        .chunked(2)
                        .forEachIndexed { i, strings ->
                            Row(
                                horizontalArrangement = Arrangement.spacedBy(2.spacing),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(IntrinsicSize.Max)
                                    .padding(horizontal = 2.5.spacing),
                            ) {
                                strings.forEachIndexed { j, messageServiceInfos ->
                                    val index = i * 2 + j

                                    EsInput(
                                        value = messageServiceInfos,
                                        onValueChange = {},
                                        enabled = false,
                                        label = if (index == 0) "Service information" else "",
                                        singleLine = false,
                                        minLines = 2,
                                        modifier = Modifier
                                            .weight(1f)
                                            .fillMaxHeight(),
                                    )
                                }
                            }
                        }
                }

                Spacer(Modifier.height(height = 3.spacing))

                EsInput(
                    value = typedMessage,
                    onValueChange = {
                        typedMessage = it
                    },
                    placeholder = "Enter message",
                    label = "Message",
                    singleLine = false,
                    minLines = 5,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 2.5.spacing),
                )

                if (isWhatsappChecked && toTelephoneNumber.isNotEmpty())
                    if (state.checkContact.isFailure) {
                        Spacer(Modifier.height(height = 2.spacing))
                        Text(
                            text = "The contact does not have a Whatsapp account",
                            style = MaterialTheme.esTypography.p1Bold16,
                            color = MaterialTheme.esColorScheme.textError,
                            modifier = Modifier.padding(horizontal = 2.5.spacing),
                        )
                    }
            }

            Row(
                horizontalArrangement = Arrangement.spacedBy(3.spacing),
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomCenter)
                    .background(Color.White)
                    .padding(1.spacing),
            ) {


                EsLargePrimaryButton(
                    onClick = {
                        if (buildFlavor.isDemo)
                            demoPopup = true
                        else {
                            if (messagesList.isNotEmpty())
                                onEvent(
                                    SendMessageEvent.SendMessageList(
                                        messageList = messagesList,
                                        type = type,
                                    ),
                                )
                        }
                    },
                    text = "Send",
                    loading = if (isWhatsappChecked && toTelephoneNumber.isNotEmpty())
                        state.checkContact.isLoading || state.sendMessageList.isLoading
                    else state.sendMessageList.isLoading,
                    enabled = if (isWhatsappChecked && toTelephoneNumber.isNotEmpty())
                        state.checkContact.isSuccess else true,
                    modifier = Modifier.weight(1f),
                )

                if (isWhatsappChecked)
                    EsLargePrimaryButton(
                        onClick = {
                            if (buildFlavor.isDemo)
                                demoPopup = true
                            else
                                openGroupPopup = !openGroupPopup
                        },
                        text = "Send to Group",
                        prefixIconRes = EsIcons.Group,
                        loading = if (isWhatsappChecked && toTelephoneNumber.isNotEmpty())
                            state.checkContact.isLoading || state.sendMessageList.isLoading
                        else state.sendMessageList.isLoading,
                        enabled = if (isWhatsappChecked && toTelephoneNumber.isNotEmpty())
                            state.checkContact.isSuccess else true,
                        modifier = Modifier.weight(1f),
                    )

                if (isWhatsappChecked && toTelephoneNumber.isNotEmpty()) {
                    EsLargeSecondaryButton(
                        onClick = {
                            if (buildFlavor.isDemo)
                                demoPopup = true
                            else {
                                if (messagesList.isNotEmpty()) {
                                    val encodedMessage = messagesList
                                        .first()
                                        .userText
                                        .encodeURLQueryComponent(encodeFull = true)

                                    uriHandler.openUri(
                                        uri = "https://api.whatsapp.com/send/?phone=$toTelephoneNumber&text=$encodedMessage&type=phone_number&app_absent=0",
                                    )
                                }
                            }
                        },
                        text = "Open Whatsapp Web",
                        enabled = state.checkContact.isSuccess,
                        loading = state.checkContact.isLoading,
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            val date = parseLocalDateOrNull(
                state.getItineraryServices.data.orEmpty()
                    .firstOrNull()?.startDate,
            ) ?: todayDate

            MessageGroupPopup(
                openDialog = openGroupPopup,
                onDismissRequest = {
                    openGroupPopup = false
                },
                primaryClient = client,
                clients = state.getAllClients.data
                    ?.filter { it.id != client.id }
                    .orEmpty(),
                travelAgent = state.getItinerary.data?.proposal?.travelAgent,
                suppliers = suppliers.filterNotNull(),
                itineraryServiceList = state.getItineraryServices.data.orEmpty(),
                groupSubject = "Be Essential Club - ${date.formatToFullString()}",
                onSend = { subject, phoneNumbers ->
                    onEvent(
                        SendMessageEvent.SendToWhatsappGroup(
                            subject = subject,
                            phoneNumbers = phoneNumbers,
                            messageList = messagesList,
                            type = type,
                        ),
                    )
                },
            )

            EssntlDemoPopup(
                isOpen = demoPopup,
                onDismissRequest = {
                    demoPopup = false
                },
            )
        }
    }
}